import type { LucideIcon } from 'lucide-react';
import { AnchorIcon, ClipboardCheckIcon, CompassIcon } from 'lucide-react';

import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';

export interface ServiceOffering {
  title: MessageFunction;
  description: MessageFunction;
  // Longer explanation shown when the offering is expanded on the services page.
  detail?: MessageFunction;
}

export interface ServiceCategory {
  slug: string;
  title: MessageFunction;
  tagline: MessageFunction;
  summary: MessageFunction;
  icon: LucideIcon;
  offerings: ServiceOffering[];
}

// Single source of truth for the home services section and the `/services` pages. Grounded in the
// inspection types AWP actually performs (see `InspectionType` in @awpmarine/core/inspections).
// NOTE: AWP is ISO 9001/45001 *certified* (a credential, shown in the accreditations strip). It does
// not offer ISO auditing as a service, so nothing here claims that.
export const serviceCategories: ServiceCategory[] = [
  {
    slug: 'vessel-inspections',
    title: m.service_inspections_title,
    tagline: m.service_inspections_tagline,
    summary: m.service_inspections_summary,
    icon: AnchorIcon,
    offerings: [
      {
        title: m.service_sire_title,
        description: m.service_sire_description,
        detail: m.service_sire_detail,
      },
      {
        title: m.service_pre_vetting_title,
        description: m.service_pre_vetting_description,
        detail: m.service_pre_vetting_detail,
      },
      {
        title: m.service_bulk_carrier_title,
        description: m.service_bulk_carrier_description,
        detail: m.service_bulk_carrier_detail,
      },
      {
        title: m.service_barge_title,
        description: m.service_barge_description,
        detail: m.service_barge_detail,
      },
      {
        title: m.service_ovid_title,
        description: m.service_ovid_description,
        detail: m.service_ovid_detail,
      },
      {
        title: m.service_flag_state_title,
        description: m.service_flag_state_description,
        detail: m.service_flag_state_detail,
      },
    ],
  },
  {
    slug: 'surveys-consultancy',
    title: m.service_surveys_title,
    tagline: m.service_surveys_tagline,
    summary: m.service_surveys_summary,
    icon: CompassIcon,
    offerings: [
      {
        title: m.service_pre_purchase_title,
        description: m.service_pre_purchase_description,
        detail: m.service_pre_purchase_detail,
      },
      {
        title: m.service_draught_title,
        description: m.service_draught_description,
        detail: m.service_draught_detail,
      },
      {
        title: m.service_cargo_supervision_title,
        description: m.service_cargo_supervision_description,
        detail: m.service_cargo_supervision_detail,
      },
      {
        title: m.service_incident_title,
        description: m.service_incident_description,
        detail: m.service_incident_detail,
      },
      {
        title: m.service_charterer_representative_title,
        description: m.service_charterer_representative_description,
        detail: m.service_charterer_representative_detail,
      },
    ],
  },
  {
    slug: 'audits-assessments',
    title: m.service_audits_title,
    tagline: m.service_audits_tagline,
    summary: m.service_audits_summary,
    icon: ClipboardCheckIcon,
    offerings: [
      {
        title: m.service_tmsa_title,
        description: m.service_tmsa_description,
        detail: m.service_tmsa_detail,
      },
      {
        title: m.service_ovmsa_title,
        description: m.service_ovmsa_description,
        detail: m.service_ovmsa_detail,
      },
      {
        title: m.service_mtmsa_title,
        description: m.service_mtmsa_description,
        detail: m.service_mtmsa_detail,
      },
      {
        title: m.service_sts_title,
        description: m.service_sts_description,
        detail: m.service_sts_detail,
      },
      {
        title: m.service_navigation_audit_title,
        description: m.service_navigation_audit_description,
        detail: m.service_navigation_audit_detail,
      },
      {
        title: m.service_office_audit_title,
        description: m.service_office_audit_description,
        detail: m.service_office_audit_detail,
      },
    ],
  },
];
